// src/components/admin/SolicitudCard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime } from '../../utils/formatDate';
import { useToast } from '../ui/ToastContext';

const estadoStyles = {
  pendiente: { pill: 'bg-yellow-100 text-yellow-800', border: 'border-l-4 border-yellow-300' },
  aprobada:  { pill: 'bg-emerald-100 text-emerald-800', border: 'border-l-4 border-emerald-300' },
  rechazada: { pill: 'bg-red-100 text-red-800',       border: 'border-l-4 border-red-300' }
};
const tipoStyles = {
  entrada: 'bg-emerald-100 text-emerald-800',
  salida:  'bg-indigo-100 text-indigo-800'
};

/* === Utils texto (tolerantes a acentos y seguros con regex) === */
const strip = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

function diacriticClassFor(ch) {
  switch (ch) {
    case 'a': return '[aàáâãäå]';
    case 'e': return '[eèéêë]';
    case 'i': return '[iìíîï]';
    case 'o': return '[oòóôõö]';
    case 'u': return '[uùúûü]';
    case 'n': return '[nñ]';
    case 'c': return '[cç]';
    default:  return ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
function termToRegex(term) {
  return Array.from(term).map(diacriticClassFor).join('');
}
const Highlighted = ({ text, tokens }) => {
  if (!text || !tokens?.length) return <>{text}</>;
  const cleanTokens = Array.from(new Set(tokens.map(strip).filter((t) => t && t.length >= 2)));
  if (!cleanTokens.length) return <>{text}</>;
  const pattern = cleanTokens.sort((a, b) => b.length - a.length).map(termToRegex).join('|');
  try {
    const re = new RegExp(`(${pattern})`, 'ig');
    const parts = String(text).split(re);
    return parts.map((p, i) =>
      i % 2 ? <mark key={i} className="rounded bg-yellow-100 px-0.5">{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>
    );
  } catch {
    return <>{text}</>;
  }
};

/* === Fecha/hora: usa timestamp si viene, si no (fecha, hora) legacy === */
function fechaHoraDisplay({ timestamp, fecha, hora }) {
  if (timestamp) {
    const d = new Date(timestamp);
    if (!isNaN(d.getTime())) {
      try {
        return d.toLocaleString('es-ES', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
      } catch { /* no-op */ }
    }
  }
  return formatDateTime(fecha, hora);
}

function SolicitudCardInner({ solicitud, onResolver, highlight }) {
  const { show } = useToast();
  const { usuario_email, tipo, fecha, hora, motivo, estado, id, timestamp } = solicitud || {};
  const [accion, setAccion] = useState(null); // 'aprobar' | 'rechazar' | null
  const [expandido, setExpandido] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const cardRef = useRef(null);

  const est = estadoStyles[estado] ?? { pill: 'bg-gray-100 text-gray-800', border: 'border-l-4 border-gray-300' };
  const textoMotivo = (motivo || '').trim();
  const esLargo = textoMotivo.length > 140;

  const tokens = useMemo(
    () => (Array.isArray(highlight) ? highlight : strip(highlight).split(/\s+/).filter(Boolean)),
    [highlight]
  );

  const onAprobar = useCallback(async () => {
    try {
      setAccion('aprobar');
      await onResolver?.(id, true);
      show('Solicitud aprobada');
    } catch {
      show('Error al aprobar');
    } finally {
      setAccion(null);
    }
  }, [onResolver, id, show]);

  const onRechazar = useCallback(async () => {
    try {
      setAccion('rechazar');
      await onResolver?.(id, false, undefined);
      show('Solicitud rechazada');
    } catch {
      show('Error al rechazar');
    } finally {
      setAccion(null);
    }
  }, [onResolver, id, show]);

  const copiarMotivo = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(textoMotivo);
      setCopiado(true);
      show('Motivo copiado');
      setTimeout(() => setCopiado(false), 1000);
    } catch { /* no-op */ }
  }, [textoMotivo, show]);

  // Atajos A/R cuando la card tiene foco (solo pendientes)
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (estado !== 'pendiente') return;
      if (e.key === 'a' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onAprobar(); }
      if (e.key === 'r' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onRechazar(); }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [estado, onAprobar, onRechazar]);

  return (
    <div
      ref={cardRef}
      className={[
        est.border,
        "rounded-2xl border border-white/40 bg-white/60 backdrop-blur-md",
        "shadow-sm hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-blue-400/60"
      ].join(" ")}
      role="listitem"
      tabIndex={0}
      aria-label={`Solicitud de ${usuario_email}, tipo ${tipo}, estado ${estado}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          {/* IZQ: datos */}
          <div className="flex-1 space-y-2">
            {/* Cabecera compacta en móvil */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-sm sm:text-[15px] text-gray-800">
              <span className="font-semibold break-all">
                <Highlighted text={usuario_email} tokens={tokens} />
              </span>
              <span className="opacity-70">solicita</span>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-[11px] sm:text-xs font-semibold uppercase tracking-wide ${tipoStyles[tipo] || 'bg-blue-100 text-blue-700'}`}
                title={`Tipo: ${tipo}`}
              >
                <Highlighted text={tipo} tokens={tokens} />
              </span>
              <span
                className={`ml-0.5 inline-block rounded-full px-2 py-0.5 text-[11px] sm:text-xs font-semibold ${est.pill}`}
                title={`Estado: ${estado}`}
              >
                {estado}
              </span>
            </div>

            {/* Fecha/Hora */}
            <p className="text-xs sm:text-[13px] text-gray-600/90">
              {fechaHoraDisplay({ timestamp, fecha, hora })}
            </p>

            {/* Motivo (colapsable si largo) */}
            {textoMotivo && (
              <div className="rounded-xl bg-white/60 backdrop-blur border border-white/50 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span aria-hidden>📝</span>
                    <span className="font-semibold">Motivo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={copiarMotivo}
                      className="text-xs rounded-md border border-gray-300/60 bg-white/80 px-2 py-1 hover:bg-white"
                      title="Copiar motivo"
                    >
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                    {esLargo && (
                      <button
                        onClick={() => setExpandido((x) => !x)}
                        className="text-xs rounded-md border border-gray-300/60 bg-white/80 px-2 py-1 hover:bg-white"
                        aria-expanded={expandido}
                        aria-controls={`motivo-${id}`}
                      >
                        {expandido ? 'Ocultar' : 'Ver más'}
                      </button>
                    )}
                  </div>
                </div>

                <div
                  id={`motivo-${id}`}
                  className={[
                    "text-sm text-gray-900 whitespace-pre-wrap",
                    "transition-[max-height] duration-200 ease-in-out",
                    expandido ? "max-h-[9999px]" : "max-h-16 overflow-hidden"
                  ].join(" ")}
                >
                  <Highlighted text={textoMotivo} tokens={tokens} />
                </div>

                {!expandido && esLargo && (
                  <div
                    aria-hidden
                    className="pointer-events-none -mt-6 h-6 w-full bg-gradient-to-t from-white/60 to-transparent"
                  />
                )}
              </div>
            )}
          </div>

          {/* DER: acciones (stack en móvil) */}
          {estado === 'pendiente' && (
            <div className="flex gap-2 self-stretch sm:self-auto sm:flex-col sm:gap-2 min-w-[220px]">
              <button
                onClick={onAprobar}
                disabled={accion !== null}
                aria-disabled={accion !== null}
                className={[
                  "flex-1 sm:w-full rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-md",
                  accion === 'aprobar' ? 'bg-emerald-400 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700',
                  "transition"
                ].join(" ")}
                aria-busy={accion === 'aprobar'}
                title="Aprobar solicitud (atajo: A)"
              >
                {accion === 'aprobar' ? 'Aprobando…' : '✅ Aprobar'}
              </button>
              <button
                onClick={onRechazar}
                disabled={accion !== null}
                aria-disabled={accion !== null}
                className={[
                  "flex-1 sm:w-full rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-md",
                  accion === 'rechazar' ? 'bg-red-400 cursor-wait' : 'bg-red-600 hover:bg-red-700',
                  "transition"
                ].join(" ")}
                aria-busy={accion === 'rechazar'}
                title="Rechazar solicitud (atajo: R)"
              >
                {accion === 'rechazar' ? 'Rechazando…' : '❌ Rechazar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ✅ Export por defecto memoizado: mantiene compat con <SolicitudCard .../> */
export default React.memo(
  SolicitudCardInner,
  (prev, next) => {
    const prevKey = Array.isArray(prev.highlight) ? prev.highlight.join('|') : prev.highlight;
    const nextKey = Array.isArray(next.highlight) ? next.highlight.join('|') : next.highlight;
    return prev.solicitud === next.solicitud && prevKey === nextKey;
  }
);